
import { DndContext, PointerSensor, useDroppable, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import IconDelete from '@mui/icons-material/Close'
import IconEdit from '@mui/icons-material/Edit'
import IconLock from '@mui/icons-material/Lock'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import EditStreamDialog from 'components/EditStreamDialog'
import { PPNavLink } from 'components/PPNavLink'
import SocialIcon from 'components/SocialIcon'
import { Stream } from 'interfaces'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink } from 'react-router'
import {
  SEARCH_FILTER_KEY_FACEBOOK,
  SEARCH_FILTER_KEY_REDDIT,
  SEARCH_FILTER_KEY_RSS
} from 'routes/search/state/types'
import { Subject, from, of as observableOf } from 'rxjs'
import { catchError, mergeMap, switchMap } from 'rxjs/operators'
import { userStreamsSelector } from 'services/content/selectors'
import { deleteStream, getUserStream, reorderStreams } from 'services/content/streams/actions'
import { message } from 'services/snackbar'
import { BRAND_TWITTER } from 'shared'
import { reorder } from 'shared/utils'
import { StoreThunkDispatch } from 'store/state'
import { sortByKeyAscending } from 'utils/sort/order'
import { AppNavLink } from '../AppNavLink'
import styles from '../AppNavigation.pcss'

interface StreamsNavMenuProps {
  expanded: boolean
  expandedStreamId?: string
  onToggleExpanded: () => void
  onToggleExpandedStream: (id: string) => void
}

export function StreamsNavMenu(props: StreamsNavMenuProps) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const intl = useIntl()
  const streams = useSelector(userStreamsSelector) as Stream[]
  const streamsMap = useMemo(() => streams.reduce((map: Record<string, Stream>, s: Stream) => {
    map[s.id] = s
    return map
  }, {}), [streams])
  const idOrder = useMemo(() => streams.slice().sort(sortByKeyAscending('order')).map(s => s.id), [streams])
  const [order, setOrder] = useState<string[]>(idOrder)
  const [streamToEdit, setStreamToEdit] = useState<Stream | null>(null)
  const deleteStream$ = useRef<Subject<string>>(undefined)
  const orderRef = useRef<string[]>(idOrder)
  const { setNodeRef: setDroppableNodeRef } = useDroppable({ id: 'droppable' })

  useEffect(() => {
    setStreamToEdit(current => {
      if (current) {
        return streams.find(s => s.id === current.id) || null
      }
      return null
    })
  }, [streams])

  useEffect(() => {
    setOrder(idOrder)
    orderRef.current = idOrder
  }, [idOrder])

  const reorder$ = useRef<Subject<string[]>>(undefined)

  useEffect(() => {
    reorder$.current = new Subject()
    reorder$.current.pipe(switchMap((ids) => {
      return from(dispatch(reorderStreams(ids)).unwrap()).pipe(catchError((error) => {
        dispatch(message(intl.formatMessage({ id: 'notifications.generic.update-failed' }), 'error'))
        return observableOf({ error })
      }))
    }))
      .subscribe((response: any) => {
        if (response.error) {
          // revert to previous ordering
          setOrder(orderRef.current)
        } else {
          setOrder(response)
          orderRef.current = response
        }
      })
  }, [dispatch, intl])

  useEffect(() => {
    deleteStream$.current = new Subject()
    deleteStream$.current.pipe(
      mergeMap(id => from(dispatch(deleteStream(id)).unwrap()).pipe(catchError((e) => {
        dispatch(message('An error occured, please try again!', 'error'))
        return observableOf({ error: e })
      }))))
      .subscribe((res: any) => {
        if (!res.error) {
          dispatch(message('Stream deleted!', 'success'))
        }
      })

    return () => deleteStream$.current?.unsubscribe()
  }, [dispatch])

  const removeStream = (id: string) => {
    deleteStream$.current?.next(id)
  }

  const editStream = (s: Stream) => {
    setStreamToEdit(s)
  }

  const closeEditStreamDialog = () => {
    setStreamToEdit(null)
  }

  const onDragEnd = (event: any) => {
    const { active, over } = event
    if (active.id !== over.id) {
      const oldIndex = order.indexOf(active.id)
      const newIndex = order.indexOf(over.id)
      const reordered = reorder(order, oldIndex, newIndex)
      setOrder(reordered)
      reorder$.current?.next(reordered)
    }
  }

  const onStreamCreateClick = () => {
    dispatch(message(intl.formatMessage({ id: 'app.nav.my-streams-create-hint' }), 'info', 5000)) // eslint-disable-line no-magic-numbers
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5
      }
    })
  )

  const onStreamUpdated = (id: string) => {
    dispatch(getUserStream({ streamId: id, force: true })).unwrap()
  }

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <SortableContext items={order} strategy={rectSortingStrategy} modifiers={[restrictToParentElement]}>
        <div ref={setDroppableNodeRef}>
          <AppNavLink
            expanded={props.expanded}
            toggleExpandedOnClick
            label={<FormattedMessage id="app.nav.my-streams" />}
            to="/content/my-streams"
            expandedItemKey={props.expandedStreamId}
            items={
              <Fragment>
                {order.map(id => {
                  const stream = streamsMap[id]
                  if (!stream) {
                    return null
                  }
                  return (
                    <DraggableStreamLink
                      key={stream.id}
                      stream={stream}
                      isExpanded={props.expandedStreamId === stream.id}
                      onEdit={editStream}
                      onRemove={removeStream}
                      onToggleExpanded={props.onToggleExpandedStream}
                    />
                  )
                })}
                <NavLink
                  to="/content/search/sources"
                  state={{
                    filters: [SEARCH_FILTER_KEY_FACEBOOK, SEARCH_FILTER_KEY_REDDIT, SEARCH_FILTER_KEY_RSS]
                  }}
                  className={`${styles['child-link']} ${styles['link-action']}`}
                  onClick={onStreamCreateClick}
                >
                  + <FormattedMessage id="app.nav.my-streams-create" />
                </NavLink>
              </Fragment>
            }
            onToggleExpanded={props.onToggleExpanded}
          />
          {Boolean(streamToEdit) && (
            <EditStreamDialog
              open
              stream={streamToEdit as Stream}
              onClose={closeEditStreamDialog}
              onUpdated={onStreamUpdated}
            />
          )}
        </div>
      </SortableContext>
    </DndContext>
  )
}

interface DraggableStreamLinkProps {
  stream: Stream
  isExpanded: boolean
  onEdit: (s: Stream) => void
  onRemove: (id: string) => void
  onToggleExpanded: (id: string) => void
}
function DraggableStreamLink(props: DraggableStreamLinkProps) {
  const { stream } = props
  const isEditable = !(stream.originalId || stream.protected)
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stream.id })

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    pointerEvents: isDragging ? 'none' : 'all'
  }

  const edit = (e: any) => {
    e.stopPropagation()
    e.preventDefault()
    props.onEdit(stream)
  }

  const remove = (e: any) => {
    e.stopPropagation()
    e.preventDefault()
    props.onRemove(stream.id)
  }

  const toggleExpanded = () => {
    props.onToggleExpanded(stream.id)
  }

  return (
    <div ref={setNodeRef} className={styles['streams-nav']} style={style as any}>
      <AppNavLink
        id={stream.id}
        key={stream.id}
        to={`/content/my-streams/${stream.id}`}
        className={styles['child-link']}
        small
        expanded={props.isExpanded}
        label={
          <div
            {...listeners}
            {...attributes}
            className={`${styles['name-box']}`}
            data-testid="stream-link"
          >
            {stream.isPrivate && <IconLock className={styles['stream-icon-lock']} />}
            <span className={`${styles.name} text-ellipsis`}>{stream.title}</span>
            <span className={styles.count}>{`(${stream.feedIds.length})`}</span>
          </div>
        }
        items={stream.feeds.map(feed => (
          <PPNavLink
            key={feed.id}
            to={`/content/my-streams/${stream.id}/feeds/${feed.id}`}
            className={styles['child-link']}
            activeClassName={styles.active}
          >
            <div className={`${styles['name-box']}`}>
              <SocialIcon outline={feed.type !== BRAND_TWITTER} circle icon={feed.type} className={styles['feed-icon']} />
              <span className={`${styles.name} text-ellipsis`}>{feed.name}</span>
            </div>
          </PPNavLink>
        ))}
        actions={
          <Fragment>
            {isEditable ? (
              <Tooltip title={<FormattedMessage id="actions.edit" />} placement="top">
                <IconButton size="small" data-action="edit" onClick={edit}>
                  <IconEdit className={styles['link-icon']} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={<FormattedMessage id="actions.delete" />} placement="top">
                <IconButton size="small" data-action="delete" onClick={remove}>
                  <IconDelete className={styles['link-icon']} />
                </IconButton>
              </Tooltip>
            )}
          </Fragment>
        }
        onToggleExpanded={toggleExpanded}
      />
    </div>
  )
}
